"use client";

import type { ChatUIMessage } from "@/types/chat";
import { toast } from "sonner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MAX_CHAT_TITLE_LENGTH } from "@/lib/chat-title";

export { MAX_CHAT_TITLE_LENGTH };

const TITLE_GENERATION_MIN_USER_MESSAGES = 2;
const FIRST_MESSAGE_MIN_CHARS_FOR_EARLY_TITLE = 10;
const TITLE_ANIMATION_MIN_DURATION = 350;
const TITLE_ANIMATION_BUFFER = 120;

type TitleSourceMessage = {
  id: string;
  role: "user" | "assistant";
  parts: [{ type: "text"; text: string }];
};

type UseChatTitleGenerationParams = {
  chatId: string | null;
  chatName?: string | null;
  incognito: boolean;
  /** True only when the chat session started with no saved chatId (brand-new chat). */
  isNewSession: boolean;
  messages: ChatUIMessage[];
  status: string;
  titleOverride?: string;
  typingSpeed: number;
  updateChatName: (
    chatId: string,
    name: string,
    options?: { manuallyEdited?: boolean }
  ) => Promise<boolean>;
};

const getMessageText = (message: ChatUIMessage) =>
  message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("\n")
    .trim();

const cleanChatTitle = (value: string) =>
  value
    .replace(/["'`]/g, "")
    .replace(/^[\s:;,.!?-]+|[\s:;,.!?-]+$/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_CHAT_TITLE_LENGTH);

const getTitleSourceMessages = (messages: ChatUIMessage[]) =>
  messages
    .filter((message) => message.role === "user" || message.role === "assistant")
    .map((message) => {
      const text = getMessageText(message);

      return text
        ? {
            id: message.id,
            role: message.role,
            parts: [{ type: "text" as const, text }],
          }
        : null;
    })
    .filter((message): message is TitleSourceMessage => message !== null);

export const useChatTitleGeneration = ({
  chatId,
  chatName,
  incognito,
  isNewSession,
  messages,
  status,
  titleOverride,
  typingSpeed,
  updateChatName,
}: UseChatTitleGenerationParams) => {
  const [generatedTitle, setGeneratedTitle] = useState<string | null>(null);
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [isTitleAnimating, setIsTitleAnimating] = useState(false);
  const requestedTitleRef = useRef(false);
  const chatIdRef = useRef<string | null>(chatId);
  const initialSessionTitleRef = useRef(cleanChatTitle(chatName ?? ""));

  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    requestedTitleRef.current = false;
    setGeneratedTitle(null);
    setIsGeneratingTitle(false);
    setIsTitleAnimating(false);
  }, [chatId]);

  const firstUserMessage = (
    messages
      .find((message) => message.role === "user")
      ?.parts.find((part) => part.type === "text") as
      | { type: "text"; text: string }
      | undefined
  )?.text;

  const titleSourceMessages = useMemo(() => getTitleSourceMessages(messages), [messages]);
  const titleSourceMessagesRef = useRef(titleSourceMessages);
  useEffect(() => {
    titleSourceMessagesRef.current = titleSourceMessages;
  }, [titleSourceMessages]);

  const userMessageCount = titleSourceMessages.filter((message) => message.role === "user").length;
  const hasMinimumUserMessages = userMessageCount >= TITLE_GENERATION_MIN_USER_MESSAGES;
  const persistedTitle = cleanChatTitle(chatName ?? "");
  const seededTitles = new Set(
    [
      isNewSession ? initialSessionTitleRef.current : "",
      cleanChatTitle(titleOverride ?? ""),
      cleanChatTitle(firstUserMessage ?? ""),
      "New chat",
    ].filter(Boolean)
  );

  const hasLongFirstMessage =
    cleanChatTitle(firstUserMessage ?? "").length > FIRST_MESSAGE_MIN_CHARS_FOR_EARLY_TITLE;

  const titleIsUnset = !persistedTitle || seededTitles.has(persistedTitle);

  // Generate once the main turn is idle so the first response does not compete
  // with a second model request. A substantive first prompt is enough context;
  // otherwise wait until the user has sent a second prompt.
  const shouldGenerateTitle =
    isNewSession &&
    !incognito &&
    !!chatId &&
    status === "ready" &&
    (hasLongFirstMessage || hasMinimumUserMessages) &&
    titleIsUnset;

  useEffect(() => {
    if (!shouldGenerateTitle || requestedTitleRef.current || !chatId) {
      return;
    }

    const activeChatId = chatId;

    requestedTitleRef.current = true;
    setIsGeneratingTitle(true);

    let isCancelled = false;

    const generateTitle = async () => {
      try {
        const response = await fetch("/api/chat/title", {
          method: "POST",
          headers: {
            "content-type": "application/json",
          },
          body: JSON.stringify({ messages: titleSourceMessagesRef.current }),
        });

        const payload = (await response.json()) as {
          error?: string;
          title?: string;
        };

        if (!response.ok) {
          throw new Error(payload.error ?? "Failed to generate chat title.");
        }

        const nextTitle = cleanChatTitle(payload.title ?? "");

        if (!nextTitle || isCancelled || chatIdRef.current !== activeChatId) {
          if (!nextTitle && !isCancelled) {
            requestedTitleRef.current = false;
          }

          return;
        }

        setGeneratedTitle(nextTitle);
        setIsTitleAnimating(true);
        setIsGeneratingTitle(false);

        const didUpdateTitle = await updateChatName(activeChatId, nextTitle, {
          manuallyEdited: false,
        });

        if (!didUpdateTitle) {
          requestedTitleRef.current = false;
          console.warn("[useChatTitleGeneration] title persisted locally but database update failed", {
            chatId: activeChatId,
            title: nextTitle,
          });
          return;
        }

        if (isCancelled || chatIdRef.current !== activeChatId) {
          return;
        }
      } catch (error) {
        requestedTitleRef.current = false;
        console.error(error);
        toast.error("Failed to generate chat title.");
      } finally {
        if (!isCancelled) {
          setIsGeneratingTitle(false);
        }
      }
    };

    void generateTitle();

    return () => {
      isCancelled = true;
    };
  }, [chatId, shouldGenerateTitle, updateChatName]);

  const title =
    generatedTitle?.slice(0, MAX_CHAT_TITLE_LENGTH) ??
    chatName?.slice(0, MAX_CHAT_TITLE_LENGTH) ??
    titleOverride?.slice(0, MAX_CHAT_TITLE_LENGTH) ??
    firstUserMessage?.slice(0, MAX_CHAT_TITLE_LENGTH) ??
    (incognito ? "Temporary chat" : "New chat");

  useEffect(() => {
    if (!isTitleAnimating) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setIsTitleAnimating(false);
    }, Math.max(TITLE_ANIMATION_MIN_DURATION, Array.from(title).length * typingSpeed + TITLE_ANIMATION_BUFFER));

    return () => {
      window.clearTimeout(timeout);
    };
  }, [isTitleAnimating, title, typingSpeed]);

  const clearGeneratedTitle = useCallback(() => {
    setGeneratedTitle(null);
  }, []);

  return {
    isGeneratingTitle,
    isTitleAnimating,
    title,
    clearGeneratedTitle,
  };
};
